import type { Styles } from '@adornis/ass/style.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-empty-placeholder.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-loader.js';
import '@adornis/chemistry/elements/components/x-tabs.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import type { DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { filterBadgeBar } from '@ils/filter-badges/client/filter-badge-bar.js';
import { Location } from '@ils/locations/db/location.js';
import { OSCE } from '@ils/osces/db/osce.js';
import { permissionGuard } from '@ils/permissions/client/permissionGuard.js';
import { hasPermission } from '@ils/permissions/hasPermission.js';
import { subscribeRatingDocsForSlot } from '@ils/ratings/api/ratings.js';
import type { ExamSlotRatingDoc } from '@ils/ratings/db/rating.js';
import { ratingConfigs } from '@ils/ratings/db/ratingConfig.js';
import { getSlotByIDWithoutCache } from '@ils/slots/api/slot.js';
import type { Slot } from '@ils/slots/db/slot.js';
import { RATING_CONFIG_NAMES } from '@ils/static/enums.js';
import { Names } from '@ils/static/names.js';
import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { customElement, property } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { from, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import type tippy from 'tippy.js';
import { resolveErrorOnAnswer } from '../../api/answer.js';
import type { AnswerError } from '../../db/answer.js';
import { ILSUser } from '../../db/ils-user.js';
import './ils-slot-detail.js';

@customElement('ils-evaluation-inspect-dialog')
export class ILSEvaluationInspectDialog extends XDialog<void> {
  static override get element_name() {
    return 'ils-evaluation-inspect-dialog';
  }

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          maxHeight: '85vh',
        },
      },
    ];
  }

  @property({ attribute: false }) slotID = new RXController<null | string>(this, null);

  @state() examSlot = new RXController(this, this.slotID.observable, slotID$ =>
    slotID$.pipe(
      switchMap(slotID =>
        slotID
          ? from(
              getSlotByIDWithoutCache(slotID)({
                osceID: 1,
                osce: { taskType: 1 },
                answer: {
                  text: 1,
                  comments: { testerID: 1 },
                  type: 1,
                  fileId: 1,
                  onlyTraineeFileId: 1,
                  reportedError: { errorType: 1, description: 1, reportedAt: 1, reportedBy: 1, locationID: 1 },
                },
                ratings: { notes: 1 },
                operatorNotes: 1,
              }),
            )
          : of(null),
      ),
    ),
  );
  @state() inspectors: ILSUser[] = [];

  override render() {
    if (this.examSlot.isLoading)
      return html`
        <x-flex padding="lg" flex center crossaxis-center ${css({ flex: '1 1 85vh' })}>
          <ils-loader></ils-loader>
        </x-flex>
      `;
    return html`
      <ils-evaluation-inspect
        .examSlot=${this.examSlot.value}
        .inspectors=${this.inspectors}
        @close=${() => this.close()}
        ${css({ width: '100%' })}
      ></ils-evaluation-inspect>
    `;
  }

  override getInnerWrapper(options: Required<DialogOptions>): HTMLDivElement {
    const innerWrapper = super.getInnerWrapper(options);
    innerWrapper.style.width = '80vmax';
    innerWrapper.style.maxWidth = '80vmax';
    return innerWrapper;
  }

  override getContentWrapper(options: Required<DialogOptions>): HTMLDivElement {
    const wrapper = super.getContentWrapper(options);
    wrapper.style.overflow = '';
    wrapper.style.maxHeight = '';
    return wrapper;
  }
}

const inspectorSelector = {
  _id: 1,
  ecommerce: { firstName: 1, lastName: 1, fullName: 1 },
  specialization: 1,
} as const satisfies BaseQLSelectionSet<ILSUser>;

type NarrowedUser = NarrowedEntity<ILSUser, typeof inspectorSelector>;
interface RatingDocWithUser {
  ratingDoc: ExamSlotRatingDoc;
  user: NarrowedUser | undefined;
}

const RatingSection = stateful(
  ({
    osce,
    ratingDocs,
    host,
  }: {
    osce: OSCE | undefined;
    ratingDocs: RatingDocWithUser[] | undefined;
    host: ChemistryLitElement;
  }) => {
    const [selectedRatingDoc, setSelectedRatingDoc] = useState<RatingDocWithUser | undefined>(ratingDocs?.[0]);
    const ratingConfig = ratingConfigs[osce?.ratingConfigName ?? RATING_CONFIG_NAMES[0]];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!ratingConfig)
      return html`
        <x-text>
          Es gibt ein Problem mit der OSCE Konfiguration von OSCE '${osce?._id}': Keine RatingConfig mit Namen
          '${osce?.ratingConfigName ?? RATING_CONFIG_NAMES[0]}' gefunden.
        </x-text>
      `;
    return html`
      <x-flex horizontal crossaxis-center space="md" padding="sm" ${css({ paddingBottom: 0 })}>
        ${filterBadgeBar({
          selectables: ratingDocs ?? [],
          renderString: (r: RatingDocWithUser) => {
            if (r.ratingDoc._class === Names.ExamSlotFinalRatingDoc._class) return 'Sync Bewertung';
            return 'Async: ' + (r.user?.ecommerce?.fullName ?? r.ratingDoc._id);
          },
          onSelect: value => {
            setSelectedRatingDoc(value);
          },
          selected: selectedRatingDoc,
        })}
      </x-flex>
      <x-flex ${css({ overflowY: 'auto', height: '100%', padding: '20px', paddingTop: '0' })}>
        <x-grid columns="max-content 1fr max-content">
          ${repeat(osce?.instructionCategories ?? [], (category, categoryIndex) => {
            const instructions = category.instructions.map(
              (instruction: { _id?: string; question: string; rating?: number; notes?: string }) => {
                const rating = selectedRatingDoc?.ratingDoc.ratings.find(
                  rating => rating.instructionId === instruction._id,
                );
                instruction.rating = rating?.score ?? -1;
                instruction.notes = rating?.notes ?? '';
                return instruction;
              },
            );
            return html`
              <x-flex padding="sm" ${css({ gridColumn: '1 / 3' })}>
                <x-h3> ${category.description} </x-h3>
              </x-flex>
              <x-flex crossaxis-center padding="sm">
                <x-text> Bewertung </x-text>
              </x-flex>
              ${repeat(
                instructions,
                (instruction, instructionIndex) => html`
                  <x-flex padding="sm" space="sm" horizontal end ${css({ gridColumn: '1' })}>
                    <x-text> ${categoryIndex + 1}.${instructionIndex + 1} </x-text>
                  </x-flex>
                  <x-flex padding="sm" space="sm" center>
                    <x-text> ${unsafeHTML(instruction.question)} </x-text>
                    ${instruction.notes
                      ? noteWrapper(
                          host,
                          'Notizen',
                          instruction.notes,
                          `Notizen zu ${categoryIndex + 1}.${instructionIndex + 1}`,
                        )
                      : nothing}
                  </x-flex>
                  <x-flex padding="sm" space="sm" horizontal crossaxis-center center>
                    ${instruction.rating === 0 || instruction.rating === 1
                      ? [...Array(ratingConfig.choices)].map(
                          (_, i) =>
                            html`
                              <x-icon
                                ${css({
                                  color: i === instruction.rating ? host.colors.accent : host.colors.neutral.shade(200),
                                })}
                              >
                                circle
                              </x-icon>
                            `,
                        )
                      : html`<x-icon
                          ${css({
                            color: host.colors.accent,
                          })}
                        >
                          auto_awesome
                          <x-tooltip><x-text>Automatisch berechnet</x-text></x-tooltip>
                        </x-icon> `}
                  </x-flex>
                `,
              )}
            `;
          })}
        </x-grid>
      </x-flex>
    `;
  },
);

@customElement('ils-evaluation-inspect')
export class ILSEvaluationInspect extends ChemistryLitElement {
  override styles() {
    return [{ ':host': { display: 'flex', flexDirection: 'column' }, p: { margin: 0 } }];
  }

  @state() private readonly _currentUser = new RXController(this, currentUser<ILSUser>());
  @state() examSlot = new RXController<Slot | undefined>(this, undefined);
  @state() osce = new RXController(
    this,
    this.examSlot.observable.pipe(
      switchMap(slot =>
        slot
          ? getByID(
              OSCE,
              slot.osceID,
            )({
              _id: 1,
              title: 1,
              titleTrainee: 1,
              osceType: 1,
              taskType: 1,
              conversationType: 1,
              enableVideo: 1,
              startButtonDuration: 1,
              durationInMinutes: 1,
              videoFileID: 1,
              text: {
                shortDescription: 1,
                sceneDescriptionTrainee: 1,
                taskDescriptionTrainee: 1,
                sceneDescriptionTester: 1,
              },
              isRatedAtLeastOnce: 1,
              ratingConfigName: 1,
              instructionCategories: { instructions: { question: 1, description: 1 }, description: 1 },
            })
          : of(undefined),
      ),
    ),
  );
  @state() location = new RXController(
    this,
    this.examSlot.observable.pipe(
      switchMap(slot => {
        const locationID = slot?.answer?.reportedError?.locationID;
        if (!locationID) return of(undefined);
        return getByID(Location, locationID)({ _id: 1, displayName: 1 });
      }),
    ),
  );
  @state() ratingDocs = new RXController(
    this,
    this.examSlot.observable.pipe(
      switchMap(slot =>
        slot
          ? subscribeRatingDocsForSlot(slot._id)({ ratings: { instructionId: 1, score: 1, notes: 1 }, inspector: 1 })
          : of([]),
      ),
      switchMap(ratingDocs =>
        ratingDocs.length
          ? getByID(
              ILSUser,
              ratingDocs.map(ratingDoc => ratingDoc.inspector).filter((x): x is string => !!x),
            )(inspectorSelector).then(users =>
              ratingDocs
                .map<RatingDocWithUser>(ratingDoc => ({
                  ratingDoc,
                  user: users.find(user => user._id === ratingDoc.inspector),
                }))
                .sort((a, b) => (a.ratingDoc._class === Names.ExamSlotFinalRatingDoc._class ? -1 : 1)),
            )
          : of([] as RatingDocWithUser[]),
      ),
      tap(ratingDocs => {
        if (ratingDocs.length > 0 && !this._selectedRatingDoc) {
          this._selectedRatingDoc = ratingDocs[0];
        }
      }),
    ),
  );
  @state() private _selectedRatingView: 'rating' | 'tasks' = 'rating';
  @state() private _selectedRatingDoc: RatingDocWithUser | undefined;

  override render() {
    if (!hasPermission(this._currentUser.value, 'ratings', 'viewothers')) return this.renderEmptyState();
    if (this.examSlot.isLoading || this.osce.isLoading || this.ratingDocs.isLoading)
      return html`
        <x-flex padding="lg" flex center crossaxis-center ${css({ flex: '1 1 85vh' })}>
          <ils-loader></ils-loader>
        </x-flex>
      `;
    if (!this.examSlot.value) return 'no slot given';
    if (!this.osce.value) return 'no osce found';
    return this.renderContent();
  }

  private renderEmptyState(): Renderable {
    return html`
      <x-empty-placeholder
        ${css({ alignSelf: 'start' })}
        no-wrapper
        .text=${'Sie haben keine Berechtigung für diesen Bereich.'}
        .img=${'close'}
      ></x-empty-placeholder>
    `;
  }

  private renderContent(): Renderable {
    return html`
      <x-grid columns="5fr max-content 2fr" space="sm" ${css({ width: '100%' })}>
        ${this.ratingDocOverview()} ${this.mediaDisplay()}
      </x-grid>
    `;
  }

  private mediaDisplay(): unknown {
    return html`
      <x-vr></x-vr>
      <x-flex padding="md" space="md">
        <x-h2> Medien </x-h2>
        ${permissionGuard({ resource: 'reportedErrors', action: 'view', guardedTemplate: this.renderReportedErrors() })}
        <ils-slot-detail
          .examSlot=${this.examSlot.value}
          @back=${() => this.dispatchEvent(new CustomEvent('close'))}
        ></ils-slot-detail>
        ${this.renderRatingNotes()}
      </x-flex>
    `;
  }

  private renderRatingNotes(): Renderable {
    if (!hasPermission(this._currentUser.value, 'ratings', 'viewothers') || !this.ratingDocs.value) return nothing;

    return html`
      <x-flex space="md">
        ${repeat(
          this.ratingDocs.value,
          r => r.ratingDoc._id,
          (r, i) =>
            r.ratingDoc._class === Names.ExamSlotFinalRatingDoc._class
              ? nothing
              : html`
                  <x-flex
                    space="xs"
                    ${css({
                      border: `1px solid ${this.colors.neutral.shade(100)}`,
                      borderRadius: this.sizes.borderRadius,
                      padding: this.spacing.sm,
                    })}
                  >
                    <x-h4> Notizen ${r.user?.ecommerce?.fullName} </x-h4>
                    <x-text> ${r.ratingDoc.notes ? unsafeHTML(r.ratingDoc.notes) : 'Keine Notizen'} </x-text>
                  </x-flex>
                `,
        )}
      </x-flex>
    `;
  }

  private renderReportedErrors(): Renderable {
    const answer = this.examSlot.value?.answer;
    if (!answer) return nothing;
    const reportedError = answer.reportedError;
    if (!reportedError) return nothing;
    const prettyErrorType = (type: AnswerError) => {
      switch (type) {
        case 'broken-both':
          return 'Beide Audiospuren kaputt';
        case 'broken-combined':
          return 'Kombiniertes Audio kaputt';
        case 'broken-trainee':
          return 'Prüflingsaudio kaputt';
        case 'missing-both':
          return 'Beide Audiospuren fehlen';
        case 'missing-combined':
          return 'Kombiniertes Audio fehlt';
        case 'missing-trainee':
          return 'Prüflingsaudio fehlt';
        case 'broken-text':
          return 'Problem mit Antworttext';
      }
    };
    return html`
      <x-flex space="xs">
        <x-flex
          space="md"
          ${css({
            border: `1px solid ${this.colors.tone.error}`,
            borderRadius: this.sizes.borderRadius,
            padding: this.spacing.sm,
            // background: this.colors.tone.error.shade(50),
          })}
        >
          <x-h2 ${css({ color: this.colors.tone.error })}> Berichteter Fehler </x-h2>
          <x-flex space="sm">
            <x-text bold> ${prettyErrorType(reportedError.errorType)}: </x-text>
            <x-text> Gemeldet am: ${reportedError.reportedAt.toLocaleString(DateTime.DATETIME_SHORT)} </x-text>
            <x-text>
              Prüfer:
              ${this.ratingDocs.value?.find(r => r.user?._id === reportedError.reportedBy)?.user?.ecommerce?.fullName}
            </x-text>
            <x-text> Standort: ${this.location.value?.displayName ?? 'Ohne Standort'} </x-text>
            <x-text> Details: ${unsafeHTML(reportedError.description)} </x-text>
          </x-flex>
        </x-flex>
        ${permissionGuard({
          resource: 'reportedErrors',
          action: 'resolve',
          guardedTemplate: html`
            <x-button
              mode="outline"
              tone="error"
              @click=${async () => {
                await XNativeDialog.guard(() => resolveErrorOnAnswer(answer._id, true)(), {
                  hasToConfirm: true,
                  confirmText: html`Die Fehlerberichte werden gelöscht und die Situation wieder zur Bewertung
                    freigegeben. <br />
                    <b>Alle bereits vorhandenen Bewertungen werden archiviert und müssen erneut bewertet werden.</b>
                    <br />
                    Bitte stellen Sie sicher, dass wirklch keine weiteren Fehler
                    ${answer.type === 'text' ? 'im Antworttext' : 'in den Aufnahmen'} vorhanden sind. <br />
                    Sind Sie sicher, dass Sie fortfahren wollen?`,
                  errorMessagePrefix: 'Fehler beim Löschen der Fehlerberichte',
                  success: () => {
                    this.dispatchEvent(new CustomEvent('close'));
                  },
                });
              }}
            >
              Fehler als behoben markieren
            </x-button>
            <x-button
              mode="outline"
              tone="error"
              @click=${async () => {
                await XNativeDialog.guard(() => resolveErrorOnAnswer(answer._id, false)(), {
                  hasToConfirm: true,
                  confirmText: html`Die Fehlerberichte werden gelöscht und die Situation wieder zur Bewertung
                    freigegeben. <br />
                    <b>Alle bereits vorhandenen Bewertungen bleiben erhalten.</b> <br />
                    Bitte stellen Sie sicher, dass wirklch keine weiteren Fehler
                    ${answer.type === 'text' ? 'im Antworttext' : 'in den Aufnahmen'} vorhanden sind. <br />
                    Sind Sie sicher, dass Sie fortfahren wollen?`,
                  errorMessagePrefix: 'Fehler beim Löschen der Fehlerberichte',
                  success: () => {
                    this.dispatchEvent(new CustomEvent('close'));
                  },
                });
              }}
            >
              Fehler verwerfen
            </x-button>
          `,
        })}
      </x-flex>
    `;
  }

  private ratingDocOverview() {
    if (!this.examSlot.value)
      return html`
        <x-empty-placeholder
          ${css({ alignSelf: 'start' })}
          no-wrapper
          .text=${'Kein Exam-Slot gefunden!'}
          .img=${'close'}
        ></x-empty-placeholder>
      `;
    if (!this.ratingDocs.value?.length)
      return html`
        <x-empty-placeholder
          ${css({ alignSelf: 'start' })}
          no-wrapper
          .text=${'Es wurden keine Bewertungsdokumente gefunden'}
          .img=${'close'}
        ></x-empty-placeholder>
      `;
    if (!this.osce.value)
      return html`
        <x-empty-placeholder
          ${css({ alignSelf: 'start' })}
          no-wrapper
          .text=${'Kein OSCE gefunden!'}
          .img=${'close'}
        ></x-empty-placeholder>
      `;
    return html`
      <x-flex ${css({ flex: '3' })}>
        <x-flex horizontal crossaxis-center space="md" padding="md" ${css({ paddingBottom: '0' })}>
          <x-button
            mode="icon"
            icon="arrow_back"
            @click=${() => this.dispatchEvent(new CustomEvent('close'))}
          ></x-button>
          <x-h1> Konsensbewertung: ${this.osce.value.titleTrainee} </x-h1>
        </x-flex>
        <x-tabs
          flex-none
          .selected=${this._selectedRatingView}
          @selected-changed=${(e: ValueEvent<'rating' | 'tasks'>) => (this._selectedRatingView = e.detail.value)}
        >
          <x-tab label="rating"> Bewertung </x-tab>
          <x-tab label="tasks"> Aufgabenstellung </x-tab>
        </x-tabs>
        <div ${css({ display: this._selectedRatingView === 'rating' ? 'contents' : 'none' })}>
          ${RatingSection({
            osce: this.osce.value,
            ratingDocs: this.ratingDocs.value,
            host: this,
          })}
        </div>
        <ils-view-osce
          flex
          ${css({ display: this._selectedRatingView === 'rating' ? 'none' : 'block' })}
          .osce=${this.osce.value}
        ></ils-view-osce>
      </x-flex>
    `;
  }
}

const noteWrapper = (host: ChemistryLitElement, label: string, noteText: string, heading: string) => {
  return html`
    <x-flex
      ${css({ cursor: 'pointer', alignSelf: 'start', padding: host.spacing.sm, margin: `-${host.spacing.sm}` })}
      @click=${() => showNoteDetail(host, noteText, heading)}
    >
      <x-text tone="subtle"> ${label} </x-text>
      <x-tooltip
        .text=${preWrapper(noteText)}
        .options=${{ placement: 'right' } satisfies Partial<tippy.Props>}
      ></x-tooltip>
    </x-flex>
  `;
};

const showNoteDetail = (host: ChemistryLitElement, text: string, heading: string) =>
  XDialog.showPopup<void>({
    content: (resolve, reject) => {
      return html`
        <x-flex space="md" padding="sm">
          <x-h3> ${heading} </x-h3>
          ${preWrapper(text)}
          <x-flex horizontal space="md" end>
            <x-button node="outline" @click=${() => resolve()}> Schließen </x-button>
          </x-flex>
        </x-flex>
      `;
    },
  });

const preWrapper = (text: string) => {
  return html`
    <pre
      ${css({
        margin: '0',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        maxWidth: '50vw',
        fontFamily: 'Lato',
      })}
    >
${text}</pre
    >
  `;
};
